import { useQuery } from '@tanstack/react-query';
import {
  getCategories,
  getCategoryTree,
  getFeaturedCategories,
  getCategoryBySlug,
  getRelatedCategories,
} from '@/api/categories';
import { useLocationStore } from '@/stores/useLocationStore';

/**
 * Query keys for categories
 */
export const categoryKeys = {
  all: ['categories'] as const,
  list: (locationId?: string) => [...categoryKeys.all, 'list', locationId] as const,
  tree: (locationId?: string) => [...categoryKeys.all, 'tree', locationId] as const,
  featured: (locationId?: string, limit?: number) => [...categoryKeys.all, 'featured', locationId, limit] as const,
  detail: (slug: string, locationId?: string) => [...categoryKeys.all, 'detail', slug, locationId] as const,
  related: (categoryId: string, locationId?: string) => [...categoryKeys.all, 'related', categoryId, locationId] as const,
};

/**
 * Hook to fetch flat list of categories
 */
export const useCategories = () => {
  const { location } = useLocationStore();

  return useQuery({
    queryKey: categoryKeys.list(location?.id),
    queryFn: () => getCategories(location?.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch hierarchical category tree
 */
export const useCategoryTree = () => {
  const { location } = useLocationStore();

  return useQuery({
    queryKey: categoryKeys.tree(location?.id),
    queryFn: () => getCategoryTree(location?.id),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch featured categories for homepage
 */
export const useFeaturedCategories = (limit: number = 6) => {
  const { location } = useLocationStore();

  return useQuery({
    queryKey: categoryKeys.featured(location?.id, limit),
    queryFn: () => getFeaturedCategories(location?.id, limit),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch category detail by slug
 */
export const useCategoryDetail = (slug: string | undefined) => {
  const { location } = useLocationStore();

  return useQuery({
    queryKey: categoryKeys.detail(slug!, location?.id),
    queryFn: () => getCategoryBySlug(slug!, location?.id),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch related categories
 */
export const useRelatedCategories = (categoryId: string | undefined, limit: number = 4) => {
  const { location } = useLocationStore();

  return useQuery({
    queryKey: categoryKeys.related(categoryId!, location?.id),
    queryFn: () => getRelatedCategories(categoryId!, location?.id, limit),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
};
